import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, FileText, FileCode, File, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import styles from './FileUpload.module.css';

export interface UploadedFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface FileUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  disabled?: boolean;
  label?: string;
  helperText?: string;
}

const SUPPORTED_EXTENSIONS = [
  '.pdf', '.docx', '.doc', '.md', '.markdown', '.mdx',
  '.txt', '.ts', '.tsx', '.js', '.jsx', '.py', '.java',
  '.go', '.rs', '.rb', '.c', '.cpp', '.h', '.cs', '.php',
  '.yaml', '.yml', '.json', '.xml', '.html', '.css', '.sql',
];

const getFileIcon = (fileName: string) => {
  const ext = fileName.toLowerCase().split('.').pop();
  if (ext === 'pdf') return <FileText size={20} className={styles.iconPdf} />;
  if (['ts', 'tsx', 'js', 'jsx', 'py', 'java', 'go', 'rs', 'rb', 'c', 'cpp', 'cs', 'php'].includes(ext || '')) {
    return <FileCode size={20} className={styles.iconCode} />;
  }
  return <File size={20} className={styles.iconDefault} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  accept = SUPPORTED_EXTENSIONS.join(','),
  multiple = true,
  maxFiles = 10,
  maxSize = 50, // 50MB default
  disabled = false,
  label = 'Upload Documents',
  helperText = 'Drag and drop files here, or click to select',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateFile = (file: File): string | null => {
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      return `Unsupported file type: ${ext}`;
    }
    if (file.size > maxSize * 1024 * 1024) {
      return `File too large. Maximum size is ${maxSize}MB`;
    }
    return null;
  };

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const currentCount = files.length;
    
    if (currentCount + fileArray.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const uploadedFiles: UploadedFile[] = fileArray.map((file) => {
      const error = validateFile(file);
      return {
        file,
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: error ? 'error' : 'pending',
        progress: 0,
        error: error || undefined,
      };
    });

    onFilesChange([...files, ...uploadedFiles]);
  }, [files, maxFiles, maxSize, onFilesChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, [disabled, addFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    // Reset input
    e.target.value = '';
  };

  const handleRemoveFile = (id: string) => {
    onFilesChange(files.filter(f => f.id !== id));
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader size={16} className={styles.spinning} />;
      case 'success':
        return <CheckCircle size={16} className={styles.successIcon} />;
      case 'error':
        return <AlertCircle size={16} className={styles.errorIcon} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      
      <div
        className={`${styles.dropzone} ${isDragging ? styles.dragging : ''} ${disabled ? styles.disabled : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          className={styles.hiddenInput}
          disabled={disabled}
        />
        
        <Upload size={32} className={styles.uploadIcon} />
        <p className={styles.dropzoneText}>{helperText}</p>
        <p className={styles.dropzoneHint}>
          Supported: PDF, DOCX, Markdown, Code files (max {maxSize}MB each)
        </p>
      </div>

      {files.length > 0 && (
        <div className={styles.fileList}>
          {files.map((uploadedFile) => (
            <div
              key={uploadedFile.id}
              className={`${styles.fileItem} ${uploadedFile.status === 'error' ? styles.fileError : ''}`}
            >
              <div className={styles.fileIcon}>
                {getFileIcon(uploadedFile.file.name)}
              </div>
              <div className={styles.fileInfo}>
                <span className={styles.fileName}>{uploadedFile.file.name}</span>
                <span className={styles.fileSize}>
                  {formatFileSize(uploadedFile.file.size)}
                  {uploadedFile.error && (
                    <span className={styles.errorText}> - {uploadedFile.error}</span>
                  )}
                </span>
                {uploadedFile.status === 'uploading' && (
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progressFill} 
                      style={{ width: `${uploadedFile.progress}%` }}
                    />
                  </div>
                )}
              </div>
              <div className={styles.fileActions}>
                {getStatusIcon(uploadedFile.status)}
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(uploadedFile.id);
                  }}
                  disabled={uploadedFile.status === 'uploading'}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
